import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { AuthContext } from "../context";
import { laboratoryServices } from "../services";
const UsersContext = createContext(null);

export function useDoctorsRefillCategories() {
  const ctx = useContext(UsersContext);
  if (!ctx) {
    throw new Error("useUsers debe usarse dentro de <UsersProvider>");
  }
  return ctx;
}

export function DoctorsRefillCategoriesProvider({ children }) {
  const { authState } = useContext(AuthContext);
  const [doctors, setDoctors] = useState([]);
  const [categories, setCategories] = useState(null);
  const [page, setPage] = useState(1);
  const [regex, setRegex] = useState("");
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(100);
  const reservedCategories = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const data = useCallback(async () => {
    try {
      setLoading(true);
      const { status, response: res } =
        await laboratoryServices.getAllDoctorsRefillCategorization({
          laboratoryId: authState.user.laboratory._id,
          regex,
          page,
          limit,
          token: authState.token,
        });
      if (status !== 200) throw new Error(res.message);
      const { response, paginationInfo } = res;
      setLoading(false);
      setPage(paginationInfo.page);
      setLimit(paginationInfo.limit);
      setCount(paginationInfo.totalDocs);
      setDoctors(response);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  }, [page, limit, regex]);
  useEffect(() => {
    data();
  }, [page, limit, regex]);
  const loadCategories = useCallback(async () => {
    laboratoryServices
      .getRefillCategories({
        laboratoryId: authState.user.laboratory._id,
        token: authState.token,
      })
      .then((res) => {
        if (res.status === 200) {
          // const categories = res.response.filter((c) => c.type !== "basal");
          const categories = res.response.reduce((acc, cur) => {
            if (cur.type === "basal") return acc;
            const products = cur.products.map((p) => ({
              ...p,
              product: p.productInfo,
            }));
            return [...acc, { ...cur, products }];
          }, []);
          setCategories(categories || null);
          reservedCategories.current = categories;
        } else {
          throw new Error(res.response);
        }
      })
      .catch((e) => console.error(e));
  }, []);
  useEffect(() => {
    loadCategories();
  }, []);

  const changePage = useCallback((p) => {
    setPage(p);
  }, []);
  const changeLimit = useCallback((l) => {
    setLimit(l);
    setPage(1);
  }, []);
  const changeRegex = useCallback((r) => {
    setRegex(r);
    setPage(1);
  }, []);
  const addDoctorCategory = useCallback(
    async (productsRefillCategoryId, doctorsToCategorize) => {
      let prevData = null;
      try {
        const { status, response } = await laboratoryServices.addDoctorCategory(
          {
            productsRefillCategoryId,
            doctorsToCategorize,
            token: authState.token,
          }
        );

        // Optimistic UI: actualizar estado local
        setDoctors((prev) => {
          prevData = prev;
          return prev.map((u) =>
            u._id === doctorsToCategorize[0].doctorId
              ? {
                  ...u,
                  categoryRefill: findCategory(productsRefillCategoryId),
                }
              : u
          );
        });
        if (status === 200) return true;
        else {
          setDoctors(prevData);
          return false;
        }
      } catch (err) {
        console.error("Error actualizando categoría:", err);
      }
    },
    []
  );
  const findCategory = useCallback((_id) => {
    const foundCategory = reservedCategories.current.find((c) => c._id === _id);
    return foundCategory ?? null;
  }, []);
  const updateDoctorCategory = useCallback(
    async (doctorId, substituteCategoryId) => {
      let prevData = null;
      try {
        const { status, response } =
          await laboratoryServices.updateDoctorCategory({
            doctorId,
            laboratoryId: authState.user.laboratory._id,
            substituteCategoryId,
            token: authState.token,
          });

        // Optimistic UI: actualizar estado local
        setDoctors((prev) => {
          prevData = prev;
          return prev.map((u) =>
            u._id === doctorId
              ? {
                  ...u,
                  categoryRefill: findCategory(substituteCategoryId),
                }
              : u
          );
        });
        if (status === 200) return true;
        else {
          setDoctors(prevData);
          return false;
        }
      } catch (err) {
        console.error("Error actualizando categoría:", err);
      }
    },
    []
  );

  const deleteDoctorCategory = useCallback(async (doctorId) => {
    try {
      let prevData = null;
      const { status, response } =
        await laboratoryServices.deleteDoctorCategory({
          doctorId,
          laboratoryId: authState.user.laboratory._id,
          token: authState.token,
        });

      // Optimistic UI
      setDoctors((prev) => {
        prevData = prev;
        return prev.map((u) =>
          u._id === doctorId ? { ...u, categoryRefill: null } : u
        );
      });
      if (status === 200) return true;
      else {
        setDoctors(prevData);
        return false;
      }
    } catch (err) {
      console.error("Error eliminando categoría:", err);
    }
  }, []);

  const value = {
    doctors,
    loading,
    error,
    // refresh: loadData,
    updateDoctorCategory,
    deleteDoctorCategory,
    categories,
    addDoctorCategory,
    changeLimit,
    changePage,
    changeRegex,
    page,
    limit,
    regex,
    count,
  };

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
}
